import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("forensiq_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("forensiq_user", JSON.stringify(data));
      setUser(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (user && !user.permissions) {
      refresh();
    }
  }, [user, refresh]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // MFA challenge — surface to caller for the second step.
      if (data.mfa_required) {
        return { mfaRequired: true, challenge: data.mfa_challenge_token,
                 mustSetup: !!data.must_setup_mfa, userHint: data.user_hint };
      }
      localStorage.setItem("forensiq_token", data.access_token);
      localStorage.setItem("forensiq_user", JSON.stringify(data.user));
      setUser(data.user);
      refresh();
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const loginMfa = async (challenge, code) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/mfa", {
        mfa_challenge_token: challenge, code,
      });
      localStorage.setItem("forensiq_token", data.access_token);
      localStorage.setItem("forensiq_user", JSON.stringify(data.user));
      setUser(data.user);
      refresh();
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("forensiq_token", data.access_token);
      localStorage.setItem("forensiq_user", JSON.stringify(data.user));
      setUser(data.user);
      refresh();
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("forensiq_token");
    localStorage.removeItem("forensiq_user");
    localStorage.removeItem("forensiq_active_tenant");
    setUser(null);
    window.location.href = "/login";
  };

  const hasPerm = (perm) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return (user.permissions || []).includes(perm);
  };

  const isSuperAdmin = () => (user?.platform_role === "super_admin");

  const setActiveTenant = (tenantId) => {
    if (!isSuperAdmin()) return;
    if (tenantId) {
      localStorage.setItem("forensiq_active_tenant", tenantId);
    } else {
      localStorage.removeItem("forensiq_active_tenant");
    }
    // Trigger a full reload so every open component re-fetches through the new scope.
    window.location.reload();
  };

  return (
    <AuthCtx.Provider value={{ user, login, loginMfa, register, logout, loading, hasPerm, refresh, isSuperAdmin, setActiveTenant }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
